import { useEffect } from "react";
import { useAtom } from "jotai";
import { commandPaletteOpenAtom } from "@/store/ui";
import { CommandPalette } from "@/features/admin/command/CommandPalette";

/**
 * CommandPaletteProvider — mounts the global admin command palette once per shell
 * and installs the Cmd/Ctrl+K keyboard shortcut that toggles it open from any
 * route (Sprint 142, TASK-4221).
 *
 * Rendered inside both AdminShell and DealerShell so the palette (and its
 * shortcut) work under `/admin/*` and `/dealer/*` alike; the palette reads the
 * active `AdminBasePathContext` to rewrite result links to the correct shell.
 */
export function CommandPaletteProvider() {
  const [open, setOpen] = useAtom(commandPaletteOpenAtom);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      // Cmd+K (macOS) / Ctrl+K (win/linux) toggles the palette. preventDefault
      // so the browser's own focus-address-bar shortcut never fires.
      const isShortcut =
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        (event.key === "k" || event.key === "K");
      if (isShortcut) {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setOpen]);

  return <CommandPalette open={open} onOpenChange={setOpen} />;
}
